import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Switch from '@/components/Switch/Switch';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { useAllVoices } from '@/hooks/useAllVoices';
import InfoCircle from '@/images/circles/InfoCirclePrimary.svg';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { IVoice, Providers } from '@d-id/types';
import styled from '@emotion/styled';

const V3ModelControlContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 20px;
    gap: 8px;
    border: ${theme.colors.black[200]} 1px solid;
    border-radius: 8px;
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    margin-bottom: -2px;
`;

const Text = styled.div`
    white-space: nowrap;
    font-size: 1.4rem;
`;

const BetaTag = styled.span`
    display: flex;
    height: 20px;
    padding: 0 4px;
    justify-content: center;
    align-items: center;
    gap: 10px;

    border-radius: 6px;
    background: ${theme.colors.black[200]};
`;

const StyledSwitch = styled(Switch)<{ checked: boolean }>`
    margin-left: auto;

    .MuiSwitch-thumb {
        background-color: white !important;
    }

    .MuiSwitch-track {
        background-color: ${({ checked }) => (checked ? theme.colors.secondary.main : theme.colors.black[300])} !important;
    }
`;

const calculateV3Stability = (currentStability: number | string | undefined): number => {
    const validValues = [0, 0.5, 1];
    const numStability = typeof currentStability === 'string' ? parseFloat(currentStability) : currentStability;

    if (numStability === undefined || isNaN(numStability)) {
        return 0;
    }

    // Find the nearest valid value
    return validValues.reduce((prev, curr) => (Math.abs(curr - numStability) < Math.abs(prev - numStability) ? curr : prev));
};

interface Props {
    voice: IVoice | null;
    setValue: (value: IVoice) => void;
    stability: number | string | undefined;
    setStability: (value: number) => void;
}

export const V3ModelControl = ({ voice, setValue, stability, setStability }: Props) => {
    const mixpanel = useAnalytics();
    const { data = [] } = useAllVoices();
    const isV3Enabled = useFlag('v3_voices', false);

    if (!isV3Enabled || voice?.provider !== Providers.Elevenlabs) {
        return null;
    }

    const handleChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const selectedVoice = data.find(voice => voice.id === voice?.id);
        const selectedVoiceConfig = typeof selectedVoice?.config === 'string' ? JSON.parse(selectedVoice?.config) : selectedVoice?.config;
        const newModelId = checked ? 'eleven_v3' : selectedVoiceConfig?.modelId;
        setValue({ ...voice, config: { ...voice?.config, modelId: newModelId } });

        // model v3 supports only stability 0 / 0.5 / 1
        if (checked) {
            const nearestStability = calculateV3Stability(stability);
            setStability(nearestStability);
        }

        mixpanel?.track('tts-v3', { event: checked ? 'checked' : 'unchecked' });
    };

    return (
        <V3ModelControlContainer>
            <Text>ElevenLabs V3</Text>
            <CustomTooltip
                placement="top"
                title="Experimental voice model. Expect more expressiveness, with possible variations in quality.">
                <StyledMaskedIcon src={InfoCircle} size="16px" color={theme.colors.secondary.main} />
            </CustomTooltip>
            <BetaTag>Beta</BetaTag>
            <StyledSwitch checked={voice?.config?.modelId === 'eleven_v3'} onChange={handleChange} />
        </V3ModelControlContainer>
    );
};
